import { useIsMobileView } from "../../utils/responsive"
import DesktopCreateStepperRaffle from "../raffle/create/desktopSteps";
import { Alert, Box, CircularProgress, Typography, Autocomplete, TextField } from "@mui/material";
import { Award, ICreationRaffle, InitialCreationRaffle, InitialCreationRaffleReset } from "../../store/interfaces/ICreationRaffle";
import { useCreateRaffleImageMutation, useCreateRaffleMutation, useGetActiveRaffleQuery, useGetAccountsQuery, useGetLastActiveRaffleQuery } from "../../store/api";
import { useEffect, useState } from "react";
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { mapICreationRaffleToDTO } from "../../store/api/utils/CreationRaffleMapper";
import { useNavigate } from "react-router-dom";
import { SnackbarKey, useSnackbar } from "notistack";
import { useAppSelector } from "../../store/hooks/useAppSelector";
import { RootState } from "../../store/types/rootState";


export const CreatePage = () => {

  const loggedInAccountId = useAppSelector(
      ({ account }: RootState) => account.id,
    );

    const accountInformation = useAppSelector(
      ({ account }: RootState) => account,
    );

    const navigate = useNavigate();
    const [creationRaffleResource, setCreationRaffleResource] = useState<ICreationRaffle>(InitialCreationRaffle);
    const [selectedAccountId, setSelectedAccountId] = useState<string>(loggedInAccountId);

    const [awards, setAwards] = useState<Award[]>([]);
    const [createRaffle, { isLoading }] = useCreateRaffleMutation();
    const [createRaffleImage] = useCreateRaffleImageMutation();
    const [disableNext, setDisableNext] = useState(false);

    // Query para obtener cuentas cuando es vip-rifas
    const { data: accounts = [] } = useGetAccountsQuery(undefined, {
      skip: accountInformation.id !== 'vip-rifas',
    });

    // Solo inicializar cuando se monta el componente o cuando cambia la cuenta seleccionada
    useEffect(() => {
        // Si es vip-rifas y hay cuentas, inicializar con la primera cuenta si aún no se ha seleccionado una
        if (accountInformation.id === 'vip-rifas' && accounts.length > 0 && selectedAccountId === loggedInAccountId) {
          setSelectedAccountId(accounts[0].id);
        }
        // Solo resetear si el resource está completamente vacío (primer montaje)
        if (!creationRaffleResource.name && creationRaffleResource.availablePaymentMethods.length === 0) {
          setCreationRaffleResource(InitialCreationRaffle);
        }
    }, [accounts, accountInformation.id]) // Removí las dependencias que causaban resets innecesarios

    // Actualizar selectedAccountId cuando cambie loggedInAccountId (si no es vip-rifas)
    useEffect(() => {
      if (accountInformation.id !== 'vip-rifas') {
        setSelectedAccountId(loggedInAccountId);
      }
    }, [loggedInAccountId, accountInformation.id]);
    

    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    const handleSnackbarClose = (key: SnackbarKey) => {
        closeSnackbar(key);
      };

      function showToast(message: string, variant: string) {
        if (variant !== "success" && variant !== "warning" && variant !== "error") {
          return;
        }
        enqueueSnackbar(message, {
          action: (key) => {
            return (
              <HighlightOffIcon
                sx={{ cursor: "pointer" }}
                onClick={() => handleSnackbarClose(key)}
              />
            );
          },
          variant: variant,
          anchorOrigin: {
            horizontal: "right",
            vertical: "top",
          },
        });
      }

      const accountId = useAppSelector(
        ({ account }: RootState) => account.id,
      );

      const isMultiRaffle = useAppSelector(
        ({ account }: RootState) => !!account.isMultiRaffle,
      );

      const { data: activeRaffleFounded, refetch } = useGetActiveRaffleQuery(accountId, {
        skip: accountId === "default" || accountId === "" || isMultiRaffle,
      });

      const accountIdToUse = selectedAccountId || loggedInAccountId;

      // Precarga en caché la última rifa para que el paso de cuentas bancarias
      // ya tenga el dato disponible al montarse
      useGetLastActiveRaffleQuery(accountIdToUse, {
        skip: accountIdToUse === "default" || accountIdToUse === "",
      });

    const handleCreateRaffle = async (validatedRaffleResource: ICreationRaffle | undefined) => {
        if (validatedRaffleResource) {
          // Usar selectedAccountId si está disponible (vip-rifas), sino usar loggedInAccountId
          validatedRaffleResource.accountId = accountIdToUse;
            try {
                const raffleCreationResponse = await createRaffle(mapICreationRaffleToDTO(validatedRaffleResource)).unwrap();
                if (raffleCreationResponse) {
                      validatedRaffleResource.awards.flatMap(element => element.images).forEach((image) => {
                        createRaffleImage({
                          accountId: accountIdToUse,
                          imageFile: image,
                          imageName: image.name,
                          raffleId: raffleCreationResponse.id || "",
                        }).then((response) => {
                          showToast('Imagen creada', 'success'); 
                        }).catch((error) => {
                          showToast('Hubo un error al crear la imagen', 'error'); 
                        });
                      });
                      showToast('Rifa creada!', 'success');
                      setCreationRaffleResource(InitialCreationRaffleReset)
                      setAwards([])
                      navigate("/dashboard");
                } else {
                  showToast('No se pudo crear la rifa en este momento!', 'warning');
                }
            } catch (error: any) {
              if (error?.data?.errors && error?.data?.errors.length > 1) {
                showToast(error?.data?.errors[0], 'error');
              } else {
                showToast('Error al momento de crear la rifa!', 'error');
                showToast('No puede tener una rifa activa primero desactivela!', 'error');
              }
            }
        }
    }
    

    const isMobile = useIsMobileView();

    if (activeRaffleFounded && !isMultiRaffle) {
        return <Box>
          <Alert severity="error"> No se pueden crear rifas nuevas si tiene una rifa activa, desactivela y vuelva a intentar. </Alert>
        </Box>
    }

    return (
      <>
        <Typography mt={2} px={10} variant="h6">
          Formulario de creación
        </Typography>
        
        {/* Selector de cuenta para vip-rifas */}
        {accountInformation.id === 'vip-rifas' && accounts.length > 0 && (
          <Box px={10} mt={2} mb={2}>
            <Autocomplete
              options={accounts}
              getOptionLabel={(option) => `${option.name} (${option.id})`}
              value={accounts.find(acc => acc.id === selectedAccountId) || null}
              onChange={(event, newValue) => {
                if (newValue) {
                  setSelectedAccountId(newValue.id);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Seleccionar Cuenta"
                  placeholder="Buscar cuenta por nombre o ID..."
                  fullWidth
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="body1" fontWeight="medium">
                      {option.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.id}
                    </Typography>
                  </Box>
                </Box>
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              noOptionsText="No se encontraron cuentas"
            />
          </Box>
        )}

        {isLoading ? (
          <Box sx={{ display: "flex", width: "100%", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : (
          <DesktopCreateStepperRaffle
            accountId={selectedAccountId || loggedInAccountId}
            awards={awards}
            setAwards={setAwards}
            resource={creationRaffleResource}
            handleCreateRaffle={handleCreateRaffle}
            setResource={setCreationRaffleResource}
            setDisableNext={setDisableNext}
            disableNext={disableNext}
          />
        )}
      </>
    );
};
    